import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import { MarkdownViewer } from '@nineeng/form-components';
import { Mail, MoreVertical, Pencil, Phone, Plus, Trash2 } from 'lucide-react';
import {
	ConfirmDialog,
	CopyToClipboardIcon,
	DataGrid,
	DataGridColumn,
	FormDialog,
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Spinner,
} from '@repo/components';
import { GroupContact, PERMISSIONS, CompanyPortalSettings } from '@repo/shared/domain';
import { AddOwnerForm, GroupContactForm } from '@repo/forms';
import { EventEmitter } from '@repo/shared/helpers';
import { getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompany, getCompanyTasks, getCompanyUsers } from 'src/api/company';
import { getCompanyPortalSettings } from 'src/api/admin/settings';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/information')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId } = Route.useParams();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();
	useBreadcrumbLevel(1, 'Information');
	const [isAddOwnerDialogOpen, setIsAddOwnerDialogOpen] = useState(false);
	const [isGroupContactDialogOpen, setIsGroupContactDialogOpen] = useState(false);
	const [isDeleteGroupContactDialogOpen, setIsDeleteGroupContactDialogOpen] = useState(false);
	const [ownerToDelete, setOwnerToDelete] = useState<string | null>(null);
	const [contactIdToDelete, setContactIdToDelete] = useState<string | null>(null);
	const [groupContactData, setGroupContactData] = useState<GroupContact | null>(null);

	const { data: group, isLoading: groupLoading } = useQuery({
		queryKey: ['company-group', groupId],
		queryFn: () => getCompany(groupId),
		enabled: !userLoading,
	});

	const { data: settings, isLoading: settingsLoading } = useQuery({
		queryKey: ['company-portal-settings'],
		queryFn: () => getCompanyPortalSettings(),
		enabled: !userLoading,
	});

	const { data: tasksResponse, isLoading: tasksLoading } = useQuery({
		queryKey: ['company-tasks', groupId],
		queryFn: () => getCompanyTasks(groupId),
		enabled: !userLoading,
	});

	if (userLoading || groupLoading || settingsLoading || tasksLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !group) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);
	const tasks = tasksResponse?.data || [];

	const revalidate = () => {
		queryClient.invalidateQueries({ queryKey: ['company-group', groupId] });
	};

	if (!settings) {
		return (
			<div className="mt-2 flex flex-col items-center justify-start">
				<div className="max-w-[1024px] flex gap-2">
					<div className="min-w-[320px]">
						<p>No settings defined</p>
					</div>
				</div>
			</div>
		);
	}

	const deleteOwner = async (ownerId: string) => {
		await request.post(`/api/company-portal/groups/${group.id}/delete-owner/${ownerId}`, {});
		EventEmitter.emit('toast', { type: 'success', message: 'Successfully removed company admin.' });
		setOwnerToDelete(null);
		revalidate();
	};

	const addOwners = async (formData: any) => {
		try {
			await request.post(`/api/company-portal/groups/${group.id}/add-owners`, formData);
			EventEmitter.emit('toast', { type: 'success', message: 'Successfully added owner(s).' });
			setIsAddOwnerDialogOpen(false);
			revalidate();
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to add owner(s).' });
		}
	};

	const handleOpenGroupContactDialog = (groupContact?: GroupContact) => {
		setIsGroupContactDialogOpen(true);
		if (groupContact) setGroupContactData(groupContact);
	};

	const handleCloseGroupContactDialog = () => {
		setIsGroupContactDialogOpen(false);
		setGroupContactData(null);
	};

	const addGroupContact = async (formData: any) => {
		try {
			await request.post(`/api/company-portal/groups/${group.id}/contact`, formData);
			EventEmitter.emit('toast', { type: 'success', message: 'Successfully added company contact.' });
			setIsGroupContactDialogOpen(false);
			setGroupContactData(null);
			revalidate();
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to add company contact.' });
		}
	};

	const updateGroupContact = async (formData: any, contactId: string) => {
		try {
			await request.put(`/api/company-portal/groups/${group.id}/contact/${contactId}`, formData);
			EventEmitter.emit('toast', { type: 'success', message: 'Successfully updated company contact.' });
			setIsGroupContactDialogOpen(false);
			setGroupContactData(null);
			revalidate();
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update company contact.' });
		}
	};

	const handleOpenDeleteGroupContactDialog = (id: string) => {
		setContactIdToDelete(id);
		setIsDeleteGroupContactDialogOpen(true);
	};

	const handleCloseDeleteGroupContactDialog = () => {
		setContactIdToDelete(null);
		setIsDeleteGroupContactDialogOpen(false);
	};

	const deleteGroupContact = async () => {
		try {
			await request.del(`/api/company-portal/groups/${group.id}/contact/${contactIdToDelete}`);
			EventEmitter.emit('toast', { type: 'success', message: 'Successfully deleted company contact.' });
			setIsDeleteGroupContactDialogOpen(false);
			setContactIdToDelete(null);
			revalidate();
		} catch (_error) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to delete company contact.' });
		}
	};

	const handleViewTask = () => {
		navigate({ to: '/company/$groupId/tasks', params: { groupId: group.id } });
	};

	const actionsMenu = (id: string, rowData: GroupContact) => (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="sm" className="flex h-6 w-6 p-0 data-[state=open]:bg-muted">
					<MoreVertical className="h-3 w-3" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-[160px]">
				<DropdownMenuItem key="edit-contact" onClick={() => handleOpenGroupContactDialog(rowData)}>
					<Pencil />
					Edit contact information
				</DropdownMenuItem>
				<DropdownMenuItem key="delete-contact" onClick={() => handleOpenDeleteGroupContactDialog(id)}>
					<Trash2 />
					Delete contact information
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);

	const columns: DataGridColumn<GroupContact>[] = [
		{ headerName: 'Type', field: 'type', flex: 1 },
		{ headerName: 'Name', field: 'name', width: 120 },
		{
			field: 'email',
			headerName: '',
			width: 10,
			renderCell: (c) => c.value && <CopyToClipboardIcon Icon={Mail} value={c.value} side="top" align="center" />,
			sortable: false,
		},
		{
			field: 'phone',
			headerName: '',
			width: 10,
			renderCell: (c) => c.value && <CopyToClipboardIcon Icon={Phone} value={c.value} side="top" align="center" />,
			sortable: false,
		},
		{
			field: 'id',
			headerName: '',
			width: 10,
			renderCell: (c) => actionsMenu(c.value, c.row),
			sortable: false,
		},
	];

	const welcomeMessage =
		settings.settings?.['welcomeMessage'] ||
		`# Welcome
	This is the default welcome message`;
	const tasksTodo = tasks.filter((t) => t.status === 'todo');
	const helpLinks = settings.settings?.helpLinks || [];
	const links = settings.settings?.links || [];
	const admins = group.admins || [];
	const companyData = [
		group?.companyData?.addressLine1,
		group?.companyData?.addressLine2,
		group?.companyData?.postalCode &&
			group?.companyData?.place &&
			`${group?.companyData?.postalCode} ${group?.companyData?.place}`,
		group?.companyData?.vatNumber,
	].filter(Boolean);

	return (
		<div className="mt-2 flex flex-col items-center justify-start">
			{tasksTodo.length > 0 && (
				<Alert
					severity="info"
					variant="filled"
					sx={{
						maxWidth: 1024,
						width: '100%',
						marginTop: 2,
						marginBottom: 2,
						'& ul': { margin: 0, paddingLeft: 2 },
					}}
					action={
						<Button
							size="sm"
							onClick={handleViewTask}
							className="text-sm font-bold bg-[inherit] shadow-none hover:bg-[inherit]"
						>
							View Tasks
						</Button>
					}
				>
					This company has open tasks to complete
				</Alert>
			)}
			<div className="max-w-[1024px] w-full flex gap-2 mt-2">
				{ownerToDelete && (
					<ConfirmDialog
						confirmAction={() => deleteOwner(ownerToDelete)}
						cancelAction={() => setOwnerToDelete(null)}
						isOpen={!!ownerToDelete}
					>
						Are you sure you want to remove this company admin?
					</ConfirmDialog>
				)}
				<div className="min-w-[320px] max-w-[480px]">
					<Paper className="p-4" variant="outlined">
						<p className="font-bold mb-1">Your Company</p>
						<p className="font-bold">{group.name}</p>
						{companyData.map((groupDataElement, i) => (
							<p key={i}>{groupDataElement}</p>
						))}
					</Paper>
					{links.length > 0 && (
						<Paper sx={{ padding: 2, marginTop: 2 }} variant="outlined">
							<p className="font-bold mb-1">Links</p>
							<div className="flex flex-col gap-1">
								{links.map((link: any) => (
									<p key={link.label}>
										<a href={link.url} target="_blank" rel="noopener noreferrer">
											{link.label}
										</a>
									</p>
								))}
							</div>
						</Paper>
					)}
					{admins.length > 0 && (
						<Paper className="p-4 mt-2" variant="outlined">
							<p className="font-bold mb-1">Your company's admins</p>
							<div className="min-h-[29px] flex flex-wrap gap-0.5">
								{admins.map((admin) => (
									<Badge
										key={admin.id}
										variant="default"
										onDelete={admin.id !== authenticatedUser.id ? () => setOwnerToDelete(admin.id) : undefined}
									>
										{admin.email}
									</Badge>
								))}
								{(verifyPermission([PERMISSIONS.companyUsersCreate]) && !(group?.legalForm === 'selfEmployed')) && (
									<Badge onClick={() => setIsAddOwnerDialogOpen(true)}>+</Badge>
								)}
							</div>
						</Paper>
					)}
					{helpLinks.length > 0 && (
						<Paper className="p-4 mt-2" variant="outlined">
							<p className="font-bold mb-1">Get Help</p>
							<div className="flex flex-col gap-1">
								{helpLinks.map((link: any) => (
									<p key={link.label}>
										<a href={link.url} target="_blank" rel="noopener noreferrer">
											{link.label}
										</a>
									</p>
								))}
							</div>
						</Paper>
					)}
				</div>
				<Paper className="p-4 flex-1 nine-task-overrides" variant="outlined">
					<MarkdownViewer value={welcomeMessage} />
				</Paper>
			</div>
			<div className="mt-4 max-w-[1024px] w-full">
				<Paper className="mt-2" variant="outlined">
					<div className="flex justify-between items-center border-b border-black/89 p-4">
						<p className="font-bold">Internal Contacts</p>
						<Button
							onClick={() => handleOpenGroupContactDialog()}
							className="bg-transparent border border-black text-black hover:bg-black/5"
						>
							<Plus /> Add Contact
						</Button>
					</div>
					<DataGrid
						data={group?.contacts || []}
						columns={columns}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						pagination
						rowCount={(group.contacts || []).length}
						onReload={revalidate}
					/>
				</Paper>
			</div>
			<AddOwnerForm
				isOpen={isAddOwnerDialogOpen}
				onClose={() => setIsAddOwnerDialogOpen(false)}
				onSubmit={addOwners}
				source="company"
			/>
			{isGroupContactDialogOpen && (
				<GroupContactForm
					isOpen={isGroupContactDialogOpen}
					onClose={handleCloseGroupContactDialog}
					onAdd={addGroupContact}
					onUpdate={updateGroupContact}
					data={groupContactData}
				/>
			)}
			<FormDialog
				isOpen={isDeleteGroupContactDialogOpen}
				onClose={handleCloseDeleteGroupContactDialog}
				title="Delete contact information"
				description="Are you sure you want to delete this contact information?"
				buttons={
					<>
						<Button variant="outline" className="bg-transparent" onClick={handleCloseDeleteGroupContactDialog}>
							Cancel
						</Button>
						<Button variant="destructive" onClick={deleteGroupContact}>
							Delete contact
						</Button>
					</>
				}
			/>
		</div>
	);
}
